import { useState, useMemo, useEffect, Fragment } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  groupByTitle,
  targetLabel,
  locationOf,
  duplicateGroupDisplayForGroup,
  duplicateGroupCountOf,
  duplicateSummaryFor,
  isDuplicateCandidateFinding,
} from '../../utils/findingTarget';
import { openFindingDetail } from '../../utils/findingNavigation';
import { exportCSV } from '../../utils/csv';
import { restoreFinding, restoreFindingErrorMessage } from '../../utils/findingRestore';
import { C, FONTS } from '../../tokens/design';
import { useFindings, useLatestScanJobId, useMetricsRollup } from '../../hooks/useClientData';
import { useClient } from '../../context/ClientContext';
import { useUser } from '../../context/UserContext';
import { useApiClient } from '../../api/apiClient';
import { useAllFindings } from '../../context/FindingsContext';
import { useViewMode } from '../../context/ViewModeContext';
import { relativeTime, exactTime } from '../../utils/time';
import { passiveEvidenceBadges } from '../../utils/passiveEvidence';
import { FINDINGS_PAGE_SIZE } from '../../utils/findingsListFiltering';
import Shell from '../../components/layout/Shell';
import Card, { CardHeader } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import EmptyState from '../../components/ui/EmptyState';
import Tag from '../../components/ui/Tag';
import SeverityBadge from '../../components/badges/SeverityBadge';
import StatusBadge from '../../components/badges/StatusBadge';
import KevBadge from '../../components/badges/KevBadge';
import PageError from '../../components/ui/PageError';
import Skeleton from '../../components/ui/Skeleton';
import { TR, TD } from '../../components/ui/Table';

const SEV_ORDER = ['critical', 'high', 'medium', 'low', 'info'];

function PassiveEvidenceTags({ finding }) {
  const colorForTone = tone => {
    if (tone === 'confirmed') return C.grn;
    if (tone === 'source') return C.sky;
    if (tone === 'caution') return C.amb;
    return C.t2;
  };
  const badges = passiveEvidenceBadges(finding);
  if (badges.length === 0) return null;
  return badges.map(badge => <Tag key={badge.key} label={badge.label} color={colorForTone(badge.tone)} />);
}

const NESSUS = { label: 'NESSUS', color: '#1470af' };
const SHODAN = { label: 'SHODAN', color: '#0ea5e9' };
const ENDPOINT = { label: 'ENDPOINT', color: '#10b981' };
const AWS = { label: 'AWS', color: '#f97316' };
const AZURE = { label: 'AZURE', color: '#3b82f6' };
const M365 = { label: 'M365', color: '#8b5cf6' };
const AD = { label: 'AD', color: '#06b6d4' };
const DAST = { label: 'DAST', color: '#ec4899' };
const NUCLEI = { label: 'NUCLEI', color: '#d946ef' };
const OSINT_B = { label: 'OSINT', color: '#14b8a6' };
const BREACH = { label: 'BREACH', color: '#f59e0b' };
const SDD = { label: 'SDD', color: '#a855f7' };

const MODULE_LABELS = {
  'vuln-nessus': NESSUS,
  'nessus-vuln': NESSUS,
  'nessus': NESSUS,
  'shodan': SHODAN,
  'connector-passive-vulnerability-lookup': ENDPOINT,
  'endpoint-connector': ENDPOINT,
  'cloud-aws-cspm': AWS,
  'aws-cspm': AWS,
  'cloud-azure-cspm': AZURE,
  'azure-cspm': AZURE,
  'm365-baseline': M365,
  'ad-assessment': AD,
  'ad-password-audit': AD,
  'ad-continuous': AD,
  'ad-attack-path': AD,
  'dast-zap': DAST,
  'dast-nuclei': NUCLEI,
  'nuclei': NUCLEI,
  'dast': DAST,
  'osint': OSINT_B,
  'breach': BREACH,
  'sdd': SDD,
};

const MODULE_FILTERS = [
  { value: 'all', label: 'All Sources' },
  { value: 'nessus', label: 'Nessus' },
  { value: 'shodan', label: 'Shodan' },
  { value: 'connector-passive-vulnerability-lookup', label: 'Endpoint Agent' },
  { value: 'aws-cspm', label: 'AWS' },
  { value: 'azure-cspm', label: 'Azure' },
  { value: 'm365-baseline', label: 'M365' },
  { value: 'ad-assessment', label: 'AD' },
  { value: 'dast-zap', label: 'DAST/ZAP' },
  { value: 'dast-nuclei', label: 'Nuclei' },
  { value: 'osint', label: 'OSINT' },
];

const CVE_SOURCE_FILTERS = [
  { value: 'all', label: 'All Sources' },
  { value: 'nessus', label: 'Nessus' },
  { value: 'shodan', label: 'Shodan' },
  { value: 'connector-passive-vulnerability-lookup', label: 'Endpoint Agent' },
];

const SAVED_VIEWS = [
  { label: 'Critical & Open', sev: 'critical', st: 'open', sort: 'sev', minEpss: null },
  { label: 'Critical & Open & EPSS > 50%', sev: 'critical', st: 'open', sort: 'epss', minEpss: 0.5 },
  { label: 'High EPSS', sev: 'all', st: 'open', sort: 'epss', minEpss: 0.5 },
  { label: 'Accepted Risk Review', sev: 'all', st: 'accepted_risk', sort: 'age', minEpss: null },
];

const FINDING_STATUS = {
  OPEN: 0,
  REMEDIATION_CLAIMED: 1,
  VERIFIED: 2,
  ACCEPTED_RISK: 3,
  POSSIBLY_REMEDIATED: 5,
};

function executiveImpactOf(f) {
  const sev = String(f?.sev || '').toLowerCase();
  if (sev === 'critical') return { impact: 'Material business risk', summary: 'Prioritize with client leadership and validate remediation this week.', cost: 'High remediation leverage' };
  if (sev === 'high') return { impact: 'Significant operational risk', summary: 'Assign an owner and schedule remediation in the current work cycle.', cost: 'Moderate remediation leverage' };
  if (sev === 'medium') return { impact: 'Limited control gap', summary: 'Track to remediation plan and confirm whether compensating controls exist.', cost: 'Targeted remediation leverage' };
  return { impact: 'Hygiene improvement', summary: 'Keep visible for trend reporting and routine hardening.', cost: 'Routine remediation leverage' };
}

function getModuleBadge(f) {
  const mod = (f?.module ?? '').toLowerCase();
  const source = (f?.source ?? f?.sourceModule ?? '').toLowerCase();
  const fp = (f?.fingerprint ?? '').toLowerCase();
  const combined = `${mod} ${source} ${fp}`;
  if (combined.includes('shodan')) return SHODAN;
  if (combined.includes('nessus')) return NESSUS;
  if (combined.includes('connector-passive-vulnerability-lookup') || combined.includes('endpoint-connector')) return ENDPOINT;
  for (const [key, val] of Object.entries(MODULE_LABELS)) {
    if (mod === key || mod.startsWith(key + '-') || mod.startsWith(key) || source === key || source.startsWith(key + '-') || source.startsWith(key)) return val;
  }
  if (fp.startsWith('nessus-') || mod.startsWith('vuln') || mod.startsWith('nessus')) return NESSUS;
  if (fp.startsWith('shodan-')) return SHODAN;
  if (fp.startsWith('cloud-aws-') || fp.startsWith('aws-')) return AWS;
  if (fp.startsWith('cloud-azure-') || fp.startsWith('azure-')) return AZURE;
  if (fp.startsWith('m365-')) return M365;
  if (fp.startsWith('ad-')) return AD;
  if (fp.startsWith('nuclei-') || mod.includes('nuclei')) return NUCLEI;
  if (fp.startsWith('dast-')) return DAST;
  if (fp.startsWith('osint-')) return OSINT_B;
  if (fp.startsWith('breach-')) return BREACH;
  if (fp.startsWith('sdd-')) return SDD;
  return null;
}

export function sourceFamily(f) {
  const badge = getModuleBadge(f);
  if (!badge) return 'Other';
  if (badge.label === 'BREACH') return 'OSINT';
  return badge.label;
}

export default function FindingsList({ all = false }) {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const frameworkFilter = searchParams.get('framework') || '';
  const { clientId } = useClient();
  const { isPlatformAdmin, isMspUser } = useUser();
  const canWrite = isPlatformAdmin || isMspUser;
  const api = useApiClient();
  const { includeSuppressed, setIncludeSuppressed } = useAllFindings();
  const { isExecutive } = useViewMode();
  const openFinding = (finding) => openFindingDetail(navigate, finding, routeLocation, all ? 'All Findings' : 'Findings');
  // Per-page independent activeOnly state so toggling here doesn't leak to
  // Cloud Security or any other module view.
  const [activeOnly, setActiveOnly] = useState(false);
  const latestJobId = useLatestScanJobId();
  const { data: rollup } = useMetricsRollup();
  const searchParam = searchParams.get('search') || '';
  const [q, setQ] = useState(searchParam);
  const [sev, setSev] = useState('all');
  const [st, setSt] = useState('all');
  const [mod, setMod] = useState('all');
  const [sort, setSort] = useState('age');
  const [minEpss, setMinEpss] = useState(null);
  const [activeView, setActiveView] = useState('');
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkMessage, setBulkMessage] = useState('');
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [collapsedGroupKeys, setCollapsedGroupKeys] = useState(() => new Set());
  const [pendingBulkAction, setPendingBulkAction] = useState(null);
  const [keyboardIndex, setKeyboardIndex] = useState(0);
  const [restoringIds, setRestoringIds] = useState(() => new Set());
  const [restoreErrors, setRestoreErrors] = useState({});

  useEffect(() => {
    setQ(searchParam);
  }, [searchParam]);

  const toggleGroup = key => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    // If expanding a group, it's no longer a "collapsed group selection"
    setCollapsedGroupKeys(prev => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const filters = {};
  if (all) filters.includeAll = true;
  if (sev !== 'all') filters.severity = sev;
  if (st !== 'all') filters.status = st;
  if (mod !== 'all') filters.module = mod;
  if (activeOnly && latestJobId) filters.latestScanJobId = latestJobId;
  if (frameworkFilter) filters.framework = frameworkFilter;
  if (minEpss != null) filters.minEpss = minEpss;
  if (q.trim()) filters.search = q;
  filters.sort = sort;
  filters.page = page;
  filters.pageSize = FINDINGS_PAGE_SIZE;

  const { data: FINDINGS, total, filteredOpen, summary, loading, error: findingsError, refetch } = useFindings(filters);

  const items = useMemo(() => {
    let f = [...FINDINGS];
    return f;
  }, [FINDINGS]);
  const groupedItems = useMemo(() => groupByTitle(items), [items]);
  const duplicateSummary = useMemo(() => duplicateSummaryFor(items), [items]);
  const visibleSeverityCounts = useMemo(() => (
    Object.fromEntries(SEV_ORDER.map(s => [s, groupedItems.filter(g => g.sev === s).length]))
  ), [groupedItems]);

  useEffect(() => {
    setKeyboardIndex(index => Math.min(index, Math.max(groupedItems.length - 1, 0)));
  }, [groupedItems.length]);

  useEffect(() => {
    const handler = (event) => {
      const tag = event.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || event.target?.isContentEditable) return;
      if (event.key === 'j' || event.key === 'J') {
        event.preventDefault();
        setKeyboardIndex(index => Math.min(index + 1, Math.max(groupedItems.length - 1, 0)));
      } else if (event.key === 'k' || event.key === 'K') {
        event.preventDefault();
        setKeyboardIndex(index => Math.max(index - 1, 0));
      } else if (event.key === 'Enter') {
        const group = groupedItems[keyboardIndex];
        if (!group) return;
        event.preventDefault();
        if (group.count > 1) toggleGroup(group.key); else openFinding(group.first);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [groupedItems, keyboardIndex]);

  const clearFrameworkFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('framework');
    setSearchParams(next, { replace: true });
  };

  const rollupModule = all ? rollup?.findings : null;
  const cnt = v => Number(summary?.severity?.[v] ?? visibleSeverityCounts[v] ?? 0);
  const sourceCounts = useMemo(() => {
    const preferred = all ? ['NESSUS', 'OSINT', 'NUCLEI'] : ['NESSUS', 'SHODAN', 'ENDPOINT'];
    const ordered = [];
    const seen = new Set();
    const addSource = source => {
      if (!source || seen.has(source)) return;
      seen.add(source);
      ordered.push(source);
    };
    preferred.forEach(addSource);
    Object.keys(summary?.source ?? {}).forEach(addSource);
    groupedItems.forEach(group => addSource(sourceFamily(group.first)));
    return ordered.map(source => {
      const rows = groupedItems.filter(g => sourceFamily(g.first) === source);
      const pageCounts = Object.fromEntries(SEV_ORDER.map(s => [s, rows.filter(g => g.sev === s).length]));
      const fullCounts = summary?.sourceSeverity?.[source];
      const counts = Object.fromEntries(SEV_ORDER.map(s => [s, Number(fullCounts?.[s] ?? pageCounts[s] ?? 0)]));
      return { source, total: Number(summary?.source?.[source] ?? rows.length), counts };
    });
  }, [groupedItems, all, summary]);
  const applySavedView = (view) => {
    setActiveView(view.label);
    setSev(view.sev);
    setSt(view.st);
    setSort(view.sort);
    setMinEpss(view.minEpss);
    setPage(1);
  };
  const clearSavedView = () => {
    setActiveView('');
    setMinEpss(null);
  };
  const matchingTotal = total ?? summary?.total ?? rollupModule?.total ?? FINDINGS.length;
  const displayTotal = groupedItems.length;
  const visibleFindingTotal = items.length;
  const totalQualifier = matchingTotal > visibleFindingTotal ? ` / ${matchingTotal} matching` : '';
  const totalPages = total > 0 ? Math.ceil(total / FINDINGS_PAGE_SIZE) : 1;
  const openCount = groupedItems.filter(g => g.findings.some(f => f.status === 'open')).length;

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  const tableHeaders = isExecutive
    ? ['', 'Finding', 'Business Impact', 'Severity', 'Status', 'Owner View', 'Discovered', '']
    : ['', 'Finding', 'Severity', 'Status', 'EPSS', 'CVE', 'Module', 'Discovered', ''];
  const tableColSpan = tableHeaders.length + 1;
  const moduleFilters = all ? MODULE_FILTERS : CVE_SOURCE_FILTERS;

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map(f => f.id)));
    }
  };

  const handleBulkStatus = async (newStatus, ids) => {
    const target = ids || [...selectedIds];
    if (target.length === 0) return;
    setBulkLoading(true);
    setBulkMessage('');
    try {
      await api.post(`/clients/${clientId}/findings/bulk-status`, {
        findingIds: target,
        status: newStatus,
      });
      setBulkMessage(`${target.length} findings updated successfully.`);
      setSelectedIds(new Set());
      setCollapsedGroupKeys(new Set());
      refetch();
    } catch (err) {
      setBulkMessage(`Failed: ${err.message}`);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkSuppress = async (ids) => {
    const target = ids || [...selectedIds];
    if (target.length === 0) return;
    setBulkLoading(true);
    setBulkMessage('');
    try {
      await Promise.all(target.map(fid =>
        api.post(`/clients/${clientId}/findings/${fid}/suppress`, { reason: 'Bulk hide from findings list' })
      ));
      setBulkMessage(`${target.length} findings hidden successfully.`);
      setSelectedIds(new Set());
      setCollapsedGroupKeys(new Set());
      refetch();
    } catch (err) {
      setBulkMessage(`Failed: ${err.message}`);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleRestoreFinding = async (findingId) => {
    if (!findingId || restoringIds.has(findingId)) return;
    setRestoringIds(prev => new Set(prev).add(findingId));
    setRestoreErrors(prev => {
      const next = { ...prev };
      delete next[findingId];
      return next;
    });
    try {
      await restoreFinding(api, clientId, findingId);
      setBulkMessage('Finding restored successfully.');
      refetch();
    } catch (err) {
      setRestoreErrors(prev => ({
        ...prev,
        [findingId]: restoreFindingErrorMessage(err),
      }));
    } finally {
      setRestoringIds(prev => {
        const next = new Set(prev);
        next.delete(findingId);
        return next;
      });
    }
  };

  const computeGroupSplit = () => {
    const groups = groupByTitle(items);
    const groupedIds = new Set();
    for (const g of groups) {
      if (collapsedGroupKeys.has(g.key) && g.count > 1) {
        g.findings.forEach(f => groupedIds.add(f.id));
      }
    }
    const individualIds = [...selectedIds].filter(id => !groupedIds.has(id));
    return { groupedCount: groupedIds.size, individualCount: individualIds.length, individualIds };
  };

  const tryBulkAction = (type, status) => {
    if (selectedIds.size === 0) return;
    if (collapsedGroupKeys.size > 0) {
      const { groupedCount, individualCount, individualIds } = computeGroupSplit();
      setPendingBulkAction({ type, status, groupedCount, individualCount, individualIds, totalCount: selectedIds.size });
      return;
    }
    if (type === 'status') handleBulkStatus(status);
    else handleBulkSuppress();
  };

  const confirmBulkAll = () => {
    const action = pendingBulkAction;
    setPendingBulkAction(null);
    if (!action) return;
    if (action.type === 'status') handleBulkStatus(action.status);
    else handleBulkSuppress();
  };

  const confirmBulkSelectedOnly = () => {
    const action = pendingBulkAction;
    setPendingBulkAction(null);
    if (!action || action.individualCount === 0) return;
    if (action.type === 'status') handleBulkStatus(action.status, action.individualIds);
    else handleBulkSuppress(action.individualIds);
  };

  const checkboxStyle = {
    width: 14, height: 14, accentColor: C.sky, cursor: 'pointer',
  };

  return (
    <Shell
      title={all ? 'All Findings' : 'Vulnerability Findings'}
      sub={loading ? 'Loading...' : all
        ? `${displayTotal} visible groups / ${visibleFindingTotal} findings${totalQualifier} \u2014 ${openCount} open groups \u2014 Operational findings excluding Sensitive Data`
        : `${displayTotal} visible vulnerability groups / ${visibleFindingTotal} findings${totalQualifier} \u2014 ${openCount} open groups \u2014 Scanner sources included`}
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 12 }}>
        {[{ l: 'Critical', v: cnt('critical'), c: C.red }, { l: 'High', v: cnt('high'), c: C.org }, { l: 'Medium', v: cnt('medium'), c: C.amb }, { l: 'Low', v: cnt('low'), c: C.sky }].map(s => (
          <Card key={s.l} onClick={() => { setSev(sev === s.l.toLowerCase() ? 'all' : s.l.toLowerCase()); setPage(1); }} style={{ padding: '10px 14px', borderTop: `2px solid ${s.c}`, cursor: 'pointer' }}>
            <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 5 }}>{s.l}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: s.c, fontFamily: FONTS.mono }}>{s.v}</div>
          </Card>
        ))}
      </div>

      <Card style={{ padding: '12px 14px', marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 900, color: C.t1, fontFamily: FONTS.sans }}>Saved Views</div>
            <div style={{ fontSize: 10, color: C.t2, marginTop: 2 }}>Fast cuts for analyst workflows and client review.</div>
          </div>
          {activeView && (
            <button onClick={clearSavedView} style={{ background: 'none', border: `1px solid ${C.b1}`, borderRadius: 5, color: C.sky, cursor: 'pointer', fontSize: 10, fontFamily: FONTS.mono, padding: '4px 8px' }}>
              Clear saved view
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {SAVED_VIEWS.map(view => (
            <button key={view.label} onClick={() => applySavedView(view)} style={{
              border: `1px solid ${activeView === view.label ? C.sky : C.b1}`,
              background: activeView === view.label ? `${C.sky}14` : C.s2,
              color: activeView === view.label ? C.sky : C.t1,
              borderRadius: 999,
              padding: '5px 10px',
              cursor: 'pointer',
              fontSize: 10,
              fontFamily: FONTS.sans,
              fontWeight: 700,
            }}>
              {view.label}
            </button>
          ))}
        </div>
      </Card>

      <Card style={{ padding: '12px 14px', marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 900, color: C.t1, fontFamily: FONTS.sans, marginBottom: 10 }}>Severity by Source</div>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(sourceCounts.length, 4)}, 1fr)`, gap: 10 }}>
          {sourceCounts.map(row => (
            <div key={row.source} style={{ border: `1px solid ${C.b1}`, borderRadius: 7, padding: 10, background: C.s2 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 10, fontWeight: 900, color: C.t1, fontFamily: FONTS.mono }}>{row.source}</span>
                <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{row.total} total</span>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[
                  { k: 'critical', c: C.red },
                  { k: 'high', c: C.org },
                  { k: 'medium', c: C.amb },
                  { k: 'low', c: C.sky },
                ].map(sevRow => (
                  <span key={sevRow.k} style={{ fontSize: 9, color: sevRow.c, background: `${sevRow.c}12`, border: `1px solid ${sevRow.c}33`, borderRadius: 4, padding: '2px 6px', fontFamily: FONTS.mono }}>
                    {sevRow.k[0].toUpperCase()} {row.counts[sevRow.k] || 0}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {duplicateSummary.candidateCount > 0 && (
        <Card style={{ padding: '10px 14px', marginBottom: 12, borderLeft: `3px solid ${C.amb}`, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 900, color: C.t1, fontFamily: FONTS.sans }}>Consolidation Candidates</div>
            <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, marginTop: 2 }}>
              {duplicateSummary.candidateCount} visible finding{duplicateSummary.candidateCount !== 1 ? 's' : ''} flagged by backend duplicate metadata
            </div>
          </div>
          <div style={{ flex: 1 }} />
          {duplicateSummary.groupCount > 0 && (
            <Tag label={`${duplicateSummary.groupCount} group${duplicateSummary.groupCount !== 1 ? 's' : ''}`} color={C.amb} />
          )}
          {duplicateSummary.groupedFindingCount > 0 && (
            <Tag label={`${duplicateSummary.groupedFindingCount} grouped`} color={C.sky} />
          )}
        </Card>
      )}

      <Card style={{ padding: '10px 14px', marginBottom: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search findings, assets, CVEs, targets..." style={{ flex: 1, minWidth: 180, background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6, padding: '6px 10px', color: C.t1, fontSize: 12, fontFamily: FONTS.mono, outline: 'none' }} />
        <select value={sev} onChange={e => { setSev(e.target.value); setActiveView(''); setPage(1); }} style={{ background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6, padding: '6px 9px', color: C.t1, fontSize: 11, fontFamily: FONTS.mono, cursor: 'pointer' }}>
          {['all', 'critical', 'high', 'medium', 'low', 'info'].map(o => <option key={o} value={o}>{o === 'all' ? 'All Severity' : o.charAt(0).toUpperCase() + o.slice(1)}</option>)}
        </select>
        <select value={mod} onChange={e => { setMod(e.target.value); setActiveView(''); setPage(1); }} style={{ background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6, padding: '6px 9px', color: C.t1, fontSize: 11, fontFamily: FONTS.mono, cursor: 'pointer' }}>
          {moduleFilters.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={st} onChange={e => { setSt(e.target.value); setActiveView(''); setPage(1); }} style={{ background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6, padding: '6px 9px', color: C.t1, fontSize: 11, fontFamily: FONTS.mono, cursor: 'pointer' }}>
          {['all', 'open', 'reopened', 'remediation_claimed', 'possibly_remediated', 'verified', 'accepted_risk', 'suppressed'].map(o => <option key={o} value={o}>{o === 'all' ? 'All Status' : o.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
        </select>
        <select value={sort} onChange={e => { setSort(e.target.value); setActiveView(''); }} style={{ background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6, padding: '6px 9px', color: C.t1, fontSize: 11, fontFamily: FONTS.mono, cursor: 'pointer' }}>
          <option value="age">Newest</option><option value="sev">Severity</option><option value="epss">EPSS</option>
        </select>
        <Button variant="sec" style={{ fontSize: 10, whiteSpace: 'nowrap' }} onClick={() => exportCSV(items.map(f => ({
          ID: f.id,
          Title: f.title,
          Asset: f.asset,
          Severity: f.sev ? f.sev.charAt(0).toUpperCase() + f.sev.slice(1) : '',
          Status: f.status ? f.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '',
          EPSS: f.ep ?? '',
          CVE: f.cve ?? '',
          EvidenceConfidence: f.evidenceConfidence ?? '',
          Module: f.module ?? '',
          Discovered: f.age ? new Date(f.age).toLocaleString() : '',
        })), 'findings-export.csv')} disabled={items.length === 0}>
          Export CSV
        </Button>
        <select value={activeOnly ? 'latest' : 'all'} onChange={e => setActiveOnly(e.target.value === 'latest')} style={{ background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6, padding: '6px 9px', color: C.t1, fontSize: 11, fontFamily: FONTS.mono, cursor: 'pointer' }}>
          <option value="latest">{all ? 'Latest scan only' : 'Latest scan findings'}</option>
          <option value="all">{all ? 'All time' : 'All findings'}</option>
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div onClick={() => setIncludeSuppressed(!includeSuppressed)} style={{ width: 28, height: 14, borderRadius: 7, background: includeSuppressed ? C.amb : C.t3, cursor: 'pointer', position: 'relative', transition: 'background 0.15s' }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: includeSuppressed ? 16 : 2, transition: 'left 0.15s' }} />
          </div>
          <span style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, whiteSpace: 'nowrap' }}>Show Hidden</span>
        </div>
      </Card>

      {/* Bulk action bar: write actions require admin */}
      {selectedIds.size > 0 && canWrite && (
        <Card style={{ padding: '8px 14px', marginBottom: 12, display: 'flex', gap: 10, alignItems: 'center', borderLeft: `3px solid ${C.sky}` }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: C.t1, fontFamily: FONTS.mono }}>{selectedIds.size} finding{selectedIds.size !== 1 ? 's' : ''} selected</span>
          <div style={{ flex: 1 }} />
          <Button variant="pri" style={{ fontSize: 10, padding: '4px 10px', background: C.amb }} disabled={bulkLoading} onClick={() => tryBulkAction('status', FINDING_STATUS.REMEDIATION_CLAIMED)}>
            Claim Remediation
          </Button>
          <Button variant="pri" style={{ fontSize: 10, padding: '4px 10px' }} disabled={bulkLoading} onClick={() => tryBulkAction('status', FINDING_STATUS.VERIFIED)}>
            Mark Resolved
          </Button>
          <Button variant="sec" style={{ fontSize: 10, padding: '4px 10px', color: C.amb }} disabled={bulkLoading} onClick={() => tryBulkAction('status', FINDING_STATUS.ACCEPTED_RISK)}>
            Accept Risk
          </Button>
          <Button variant="sec" style={{ fontSize: 10, padding: '4px 10px', color: C.red }} disabled={bulkLoading} onClick={() => tryBulkAction('suppress')}>
            Hide Selected
          </Button>
          <Button variant="sec" style={{ fontSize: 10, padding: '4px 10px' }} onClick={() => setSelectedIds(new Set())}>
            Clear
          </Button>
        </Card>
      )}

      {bulkMessage && (
        <div style={{
          padding: '8px 14px', marginBottom: 12, borderRadius: 6, fontSize: 11, fontFamily: FONTS.mono,
          background: bulkMessage.startsWith('Failed') ? C.redBg : `${C.grn}12`,
          border: `1px solid ${bulkMessage.startsWith('Failed') ? C.red : C.grn}33`,
          color: bulkMessage.startsWith('Failed') ? C.red : C.grn,
        }}>
          {bulkMessage}
        </div>
      )}

      {findingsError && <PageError error={findingsError} onRetry={refetch} />}

      {frameworkFilter && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', marginBottom: 10, background: `${C.sky}12`, border: `1px solid ${C.sky}33`, borderRadius: 6 }}>
          <span style={{ fontSize: 11, color: C.sky, fontFamily: FONTS.mono, fontWeight: 600 }}>
            Filtered to framework: {frameworkFilter}
          </span>
          <div style={{ flex: 1 }} />
          <button onClick={clearFrameworkFilter} style={{ background: 'none', border: `1px solid ${C.sky}55`, color: C.sky, cursor: 'pointer', fontSize: 10, fontFamily: FONTS.mono, padding: '3px 10px', borderRadius: 4 }}>
            Clear filter
          </button>
        </div>
      )}

      <div style={{ fontSize: 10, color: C.t3, fontFamily: FONTS.mono, marginBottom: 10, padding: '0 2px', display: 'flex', justifyContent: 'space-between' }}>
        <span>{all ? 'This page shows operational findings across non-SDD modules. Use the framework filter to drill into compliance-related findings.' : 'This queue includes vulnerability scanner, external exposure, and endpoint-agent findings. CVE identifiers are shown when scanner evidence includes them.'}</span>
        <span>{activeOnly ? (all ? 'Showing findings from most recent completed scan' : 'Showing vulnerability findings from most recent completed scan') : (all ? 'Showing all findings across all scans' : 'Showing vulnerability findings from every source')}</span>
      </div>

      <Card>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.b1}` }}>
              <th style={{ padding: '8px 10px', background: C.s2, width: 32 }}>
                <input
                  type="checkbox"
                  checked={items.length > 0 && selectedIds.size === items.length}
                  onChange={toggleAll}
                  style={checkboxStyle}
                />
              </th>
              {tableHeaders.map(h => (
                <th key={h} style={{ padding: '8px 12px', fontSize: 9, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: 'left', background: C.s2 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && [0, 1, 2, 3, 4].map(i => (
              <tr key={i} style={{ borderBottom: `1px solid ${C.b2}` }}>
                <TD style={{ width: 32 }}><Skeleton width={14} height={14} /></TD>
                <TD style={{ width: 18 }} />
                <TD><Skeleton width={240} height={12} /></TD>
                <TD><Skeleton width={isExecutive ? 160 : 60} height={18} /></TD>
                <TD><Skeleton width={55} height={18} /></TD>
                <TD><Skeleton width={isExecutive ? 180 : 30} height={12} /></TD>
                {!isExecutive && <TD><Skeleton width={75} height={12} /></TD>}
                {!isExecutive && <TD><Skeleton width={50} height={14} /></TD>}
                <TD><Skeleton width={55} height={12} /></TD>
                <TD style={{ width: 20 }} />
              </tr>
            ))}
            {!loading && items.length === 0 && FINDINGS.length === 0 && (
              <tr><td colSpan={tableColSpan}>
                <div style={{ padding: 32, textAlign: 'center' }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.t1, marginBottom: 8 }}>No vulnerability findings yet</div>
                  <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginBottom: 16, maxWidth: 520, margin: '0 auto 16px' }}>Results appear after vulnerability scanning, external exposure analysis, or endpoint-agent passive detection.</div>
                  <Button variant="sec" onClick={() => navigate('/client/credentials')}>Configure Scanning</Button>
                </div>
              </td></tr>
            )}
            {!loading && items.length === 0 && FINDINGS.length > 0 && (
              <tr><td colSpan={tableColSpan}>
                <EmptyState title="No findings match your filters" sub="Try adjusting search or filter criteria" />
              </td></tr>
            )}
            {groupedItems.map((g, groupIndex) => {
              const f = g.first;
              const isSuppressed = f.status === 'suppressed' || f.suppressed;
              const isExp = expandedGroups.has(g.key);
              const groupIds = g.findings.map(x => x.id);
              const allSelected = groupIds.every(id => selectedIds.has(id));
              const exec = executiveImpactOf(f);
              const isKeyboardActive = groupIndex === keyboardIndex;
              const duplicateCandidateCount = g.findings.filter(isDuplicateCandidateFinding).length;
              const duplicateGroupCount = Math.max(...g.findings.map(duplicateGroupCountOf), duplicateCandidateCount);
              const duplicateLabel = duplicateGroupCount > 1 ? `Duplicate x${duplicateGroupCount}` : (duplicateCandidateCount > 0 ? 'Duplicate candidate' : '');
              const groupDisplay = duplicateGroupDisplayForGroup(g);
              const isRestoring = restoringIds.has(f.id);
              const restoreError = restoreErrors[f.id];
              return (
                <Fragment key={g.key}>
                  <TR
                    onClick={() => g.count > 1 ? toggleGroup(g.key) : openFinding(f)}
                    style={{
                      ...(isSuppressed ? { opacity: 0.5 } : {}),
                      background: isKeyboardActive ? `${C.sky}10` : undefined,
                      boxShadow: isKeyboardActive ? `inset 2px 0 0 ${C.sky}` : undefined,
                    }}
                  >
                    <TD style={{ width: 32, padding: '8px 10px' }} onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={() => {
                          const wasSelected = allSelected;
                          setSelectedIds(prev => {
                            const next = new Set(prev);
                            if (wasSelected) groupIds.forEach(id => next.delete(id));
                            else groupIds.forEach(id => next.add(id));
                            return next;
                          });
                          if (!isExp && g.count > 1) {
                            setCollapsedGroupKeys(prev => {
                              const next = new Set(prev);
                              if (wasSelected) next.delete(g.key);
                              else next.add(g.key);
                              return next;
                            });
                          }
                        }}
                        style={checkboxStyle}
                      />
                    </TD>
                    <TD style={{ width: 18, color: C.t2, textAlign: 'center' }}>
                      {g.count > 1 ? (isExp ? <ChevronDown size={12} /> : <ChevronRight size={12} />) : null}
                    </TD>
                    <TD style={{ maxWidth: 360 }}>
                      <div style={{ fontSize: 13, color: C.t1, fontWeight: 600, textDecoration: isSuppressed ? 'line-through' : 'none', lineHeight: 1.35 }}>
                        {f.title}
                        {g.count > 1 && <span style={{ fontSize: 10, color: C.sky, fontFamily: FONTS.mono, marginLeft: 8, fontWeight: 700 }}>({g.count} findings)</span>}
                        {duplicateLabel && <span style={{ fontSize: 9, color: C.amb, fontFamily: FONTS.mono, marginLeft: 8, fontWeight: 800, padding: '2px 6px', borderRadius: 4, background: `${C.amb}12`, border: `1px solid ${C.amb}33`, whiteSpace: 'nowrap' }}>{duplicateLabel}</span>}
                      </div>
                      {(groupDisplay || targetLabel(f, g.count) || isSuppressed) && (
                        <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, marginTop: 3, display: 'flex', gap: 6, alignItems: 'center' }}>
                          {groupDisplay && <span style={{ color: C.t3 }}>{groupDisplay.label}</span>}
                          {targetLabel(f, g.count)}
                          {isSuppressed && <Tag label="Hidden" color={C.amb} />}
                          {isRestoring && <span style={{ fontSize: 9, color: C.sky, fontFamily: FONTS.mono }}>Restoring...</span>}
                        </div>
                      )}
                      {restoreError && (
                        <div style={{ fontSize: 10, color: C.red, fontFamily: FONTS.mono, marginTop: 4, lineHeight: 1.35 }}>
                          {restoreError}
                        </div>
                      )}
                    </TD>
                    <TD><SeverityBadge value={g.sev} /></TD>
                    <TD><StatusBadge value={f.status} /></TD>
                    {isExecutive ? (
                      <>
                        <TD>
                          <div style={{ fontSize: 11, color: C.t1, fontWeight: 800, fontFamily: FONTS.sans }}>{exec.impact}</div>
                          <div style={{ fontSize: 10, color: C.grn, marginTop: 2, fontFamily: FONTS.sans }}>{exec.cost}</div>
                        </TD>
                        <TD style={{ maxWidth: 260 }}>
                          <div style={{ fontSize: 11, color: C.t2, lineHeight: 1.45, fontFamily: FONTS.sans }}>{exec.summary}</div>
                        </TD>
                      </>
                    ) : (
                      <>
                        <TD>
                          {f.ep != null && f.ep > 0
                            ? <span style={{ fontSize: 10, fontWeight: 700, color: f.ep >= 0.5 ? C.red : f.ep >= 0.1 ? C.org : C.t2, fontFamily: FONTS.mono }}>{Math.round(f.ep * 100)}%</span>
                            : <span style={{ color: C.t3, fontSize: 10, fontFamily: FONTS.mono }}>{'\u2014'}</span>
                          }
                        </TD>
                        <TD>{(() => {
                          const passiveBadges = passiveEvidenceBadges(f);
                          const cves = Array.isArray(f.cves) && f.cves.length > 0 ? f.cves : (f.cve ? [f.cve] : []);
                          const primaryCve = cves[0] ?? '';
                          if (!primaryCve && passiveBadges.length === 0) return <span style={{ color: C.t3, fontSize: 10, fontFamily: FONTS.mono }}>{'\u2014'}</span>;
                          return (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                              {primaryCve && <Tag label={primaryCve} color={C.amb} />}
                              {cves.length > 1 && (
                                <span title={cves.slice(1).join(', ')}>
                                  <Tag label={`+${cves.length - 1} CVE${cves.length > 2 ? 's' : ''}`} color={C.sky} />
                                </span>
                              )}
                              <PassiveEvidenceTags finding={f} />
                              {f.kev && primaryCve && <KevBadge />}
                            </span>
                          );
                        })()}</TD>
                        <TD>{(() => {
                          const badge = getModuleBadge(f);
                          return badge
                            ? <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4, fontFamily: FONTS.mono, color: badge.color, background: `${badge.color}14`, border: `1px solid ${badge.color}33` }}>{badge.label}</span>
                            : <span style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono }}>{f.module || '\u2014'}</span>;
                        })()}</TD>
                      </>
                    )}
                    <TD><span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, whiteSpace: 'nowrap' }} title={f.age ? exactTime(f.age) : ''}>{f.age ? relativeTime(f.age) : '\u2014'}</span></TD>
                    <TD onClick={e => {
                      if (isSuppressed) return;
                      e.stopPropagation();
                      openFinding(f);
                    }} style={{ cursor: isSuppressed ? 'default' : 'pointer' }}>
                      {isSuppressed ? (
                        <button type="button" disabled={isRestoring} style={{ fontSize: 10, color: isRestoring ? C.t2 : C.sky, fontFamily: FONTS.mono, cursor: isRestoring ? 'wait' : 'pointer', whiteSpace: 'nowrap', background: 'none', border: 'none', padding: 0 }} onClick={e => {
                          e.stopPropagation();
                          handleRestoreFinding(f.id);
                        }}>{isRestoring ? 'Restoring...' : 'Restore'}</button>
                      ) : (
                        <span style={{ fontSize: 12, color: C.sky }}>{'\u2192'}</span>
                      )}
                    </TD>
                  </TR>
                  {isExp && (
                    <tr style={{ background: C.s2 }}>
                      <td colSpan={tableColSpan} style={{ padding: '10px 16px 14px 40px' }}>
                        <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                          Affected locations ({g.count}){groupDisplay ? ` - ${groupDisplay.label}` : ''}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {g.findings.map(x => {
                            const loc = x.asset || locationOf(x);
                            if (!loc) return null;
                            return (
                              <div key={x.id} onClick={e => { e.stopPropagation(); openFinding(x); }} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontFamily: FONTS.mono, color: C.t1, background: C.s1, border: `1px solid ${C.b2}` }}>
                                <span style={{ wordBreak: 'break-all', flex: 1 }}>{loc}</span>
                                <span style={{ fontSize: 10, color: C.t3 }}>{'\u2192'}</span>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        <div style={{ padding: '8px 14px', borderTop: `1px solid ${C.b1}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{groupedItems.length} visible groups / {items.length} findings{matchingTotal > items.length ? ` / ${matchingTotal} matching` : ''}</span>
          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <Button variant="sec" style={{ fontSize: 10, padding: '4px 8px' }} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>{'\u2190'} Prev</Button>
              <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>Page {page} of {totalPages}</span>
              <Button variant="sec" style={{ fontSize: 10, padding: '4px 8px' }} disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next {'\u2192'}</Button>
            </div>
          )}
        </div>
      </Card>
      {pendingBulkAction && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setPendingBulkAction(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 10, padding: 24, width: 420, maxWidth: '90vw' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.t1, marginBottom: 12 }}>Confirm Bulk Action</div>
            <div style={{ fontSize: 12, color: C.t2, fontFamily: FONTS.mono, marginBottom: 20, lineHeight: 1.6 }}>
              Apply to all {pendingBulkAction.totalCount} findings in the selected group{collapsedGroupKeys.size > 1 ? 's' : ''}?
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="sec" style={{ fontSize: 11, padding: '6px 14px' }} onClick={() => setPendingBulkAction(null)}>
                Cancel
              </Button>
              {pendingBulkAction.individualCount > 0 && (
                <Button variant="sec" style={{ fontSize: 11, padding: '6px 14px', color: C.amb }} onClick={confirmBulkSelectedOnly}>
                  Apply to Selected Only ({pendingBulkAction.individualCount})
                </Button>
              )}
              <Button variant="pri" style={{ fontSize: 11, padding: '6px 14px' }} onClick={confirmBulkAll}>
                Apply to All ({pendingBulkAction.totalCount})
              </Button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}
